import { useMsal } from '@azure/msal-react'
import Brightness4Icon from '@mui/icons-material/Brightness4'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { Box, Button, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { loginRequest } from '../../authConfig'
import { useColorMode } from '@src/contexts/ThemeContext'

const LoginPage = () => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const { mode, toggleColorMode } = useColorMode()
  const { instance } = useMsal()
  document.title = t('common.login')

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch(console.error)
  }

  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en'
  const isDark = mode === 'dark'

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'fixed',
        inset: 0,
        background: theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 440,
          maxWidth: '90vw',
          px: { xs: 4, sm: 6 },
          py: 6,
          borderRadius: '20px',
          background: theme.palette.background.paper,
          boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <IconButton
          onClick={toggleColorMode}
          size="small"
          aria-label={isDark ? t('common.lightMode') : t('common.darkMode')}
          sx={{ position: 'absolute', top: 12, left: 12, color: 'text.secondary' }}
        >
          {isDark ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
        </IconButton>

        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            borderRadius: '14px',
            overflow: 'hidden',
            border: (t) => `1px solid ${t.palette.divider}`,
          }}
        >
          {(['fr', 'en'] as const).map((lang) => (
            <Button
              key={lang}
              size="small"
              onClick={() => i18n.changeLanguage(lang)}
              sx={{
                minWidth: 28,
                px: 1,
                py: 0.15,
                fontSize: '0.6rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                borderRadius: 0,
                color: currentLang === lang ? 'primary.contrastText' : 'text.secondary',
                backgroundColor: currentLang === lang ? 'primary.main' : 'transparent',
                '&:hover': {
                  backgroundColor: currentLang === lang ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              {lang}
            </Button>
          ))}
        </Box>

        <Stack alignItems="center" spacing={0.5} sx={{ mt: 3, mb: 1 }}>
          <Box
            component="img"
            src={isDark ? '/images/iFinance-white.svg' : '/images/iFinance.svg'}
            alt="iFinance"
            sx={{ width: 160, mb: 1 }}
          />
          <Typography
            component="div"
            sx={{
              fontFamily: '"Quicksand", "Inter", sans-serif',
              fontWeight: 800,
              fontSize: { xs: '2rem', sm: '2.4rem' },
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              lineHeight: 1,
              color: 'text.primary',
            }}
          >
            Dev
            <Box component="span" sx={{ color: 'primary.main' }}>Hub</Box>
          </Typography>
        </Stack>

        <Box
          sx={{
            width: '60%',
            height: '1px',
            background: (t) =>
              `linear-gradient(90deg, transparent, ${t.palette.primary.main}, transparent)`,
            my: 3,
          }}
        />

        <Button
          type="button"
          variant="contained"
          fullWidth
          onClick={handleLogin}
          sx={{
            background: '#0078D4',
            color: '#FFFFFF',
            fontFamily: '"Inter", sans-serif',
            fontWeight: 600,
            fontSize: '15px',
            letterSpacing: '0.05em',
            textTransform: 'none',
            borderRadius: '12px',
            py: 1.4,
            boxShadow: 'none',
            '&:hover': { background: '#005FA3', boxShadow: 'none' },
          }}
        >
          {t('common.signInWithMicrosoft')}
        </Button>

        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 2 }}>
          <LockOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography
            sx={{ fontFamily: '"Inter", sans-serif', fontSize: '11px', color: 'text.secondary' }}
          >
            {t('common.securityNote')}
          </Typography>
        </Stack>
      </Box>
    </Box>
  )
}

export default LoginPage
